import { useState } from 'preact/hooks'
import { X, AlertTriangle, Trash2, Calendar } from 'lucide-preact'
import type { CascadeError } from '@/utils/cascadeError'

interface WebTypeCascadeModalProps {
  isOpen: boolean
  error: CascadeError | null
  /** Convert each blocked rule to a fixed-date rule, then retry the parent Web patch. */
  onConvert: () => Promise<void>
  /** Delete each blocked touchpoint, then retry the parent Web patch. */
  onDelete: () => Promise<void>
  /** Close the modal without retrying. */
  onCancel: () => void
}

const HEADINGS: Record<CascadeError['errorCode'], string> = {
  OnlineEventDateRequiredByEmailRule:
    'Removing this date will orphan scheduled emails',
  OnlineEventModeChangeOrphansEmailRule:
    'Switching the event mode will orphan scheduled emails',
}

export function WebTypeCascadeModal({
  isOpen,
  error,
  onConvert,
  onDelete,
  onCancel,
}: WebTypeCascadeModalProps) {
  const [busy, setBusy] = useState<'convert' | 'delete' | null>(null)
  const [convertNote, setConvertNote] = useState<string | null>(null)

  if (!isOpen || !error) return null

  const handleConvert = async () => {
    setConvertNote('Convert-to-fixed-dates is coming soon. Use Delete or Cancel for now.')
    setBusy('convert')
    try {
      await onConvert()
    } finally {
      setBusy(null)
    }
  }

  const handleDelete = async () => {
    setBusy('delete')
    try {
      await onDelete()
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {HEADINGS[error.errorCode]}
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {error.blockedBy.length} email {error.blockedBy.length === 1 ? 'touchpoint depends' : 'touchpoints depend'} on the field you're changing.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy !== null}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg p-1 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <ul className="space-y-2">
            {error.blockedBy.map(tp => (
              <li
                key={tp.touchPointId}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {tp.name}
                  </div>
                  {tp.ruleSummary && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {tp.ruleSummary}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {convertNote && (
            <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              {convertNote}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy !== null}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConvert}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors disabled:opacity-50"
          >
            <Calendar className="w-4 h-4" />
            {busy === 'convert' ? 'Converting…' : 'Convert to fixed dates'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {busy === 'delete' ? 'Deleting…' : `Delete ${error.blockedBy.length === 1 ? 'this touchpoint' : 'these touchpoints'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
